// Copyright (c) 2024-2026 AlphaGrade. All Rights Reserved.
//
// Public SMS / Text Alerts opt-in page. Rendered by LoginGate for /sms-alerts
// regardless of auth so it's reachable by logged-out visitors and — critically —
// by carrier / TCR vetting during A2P 10DLC campaign review.
//
// WHY THIS PAGE EXISTS: our real opt-in lives inside the authenticated account,
// which a TCR reviewer cannot see. The first 10DLC campaign was rejected for
// "issues verifying the Call to Action (CTA)" for exactly that reason. This page
// makes the CTA publicly verifiable: it shows the program, a visual mock of the
// in-app opt-in form, the exact consent language, message frequency, rates,
// STOP/HELP, sample messages, and links to Terms + Privacy (the no-third-party-
// sharing clause lives in /privacy). Reference this URL in Twilio's Campaign CTA.
//
// 2026-09-10 - rewritten after rejection #3, Twilio error 30907 ("the website URL
// in your registration doesn't match the brand, campaign purpose, consent flow,
// or sample messages"). Two mismatches were real:
//   1. The campaign declares THREE message types (verification code, portfolio
//      regime alerts, Grade Watch) but this page described only portfolio alerts
//      and showed sample text that matched nothing we actually send.
//   2. The opt-in mock showed a toggle called "Text me portfolio alerts" that
//      does not exist. The real control is "Text me alerts too" in the Portfolio
//      Alerts section (CorrelationsPage.js), with Send code -> Verify.
//
// ANYONE EDITING THIS PAGE: the sample messages below must stay byte-identical to
// the samples registered on the A2P campaign AND to the bodies actually sent by
// backend/routers/alerts.py, services/alert_runner.py and
// services/grade_watch_runner.py. If those three ever disagree, TCR rejects.

import React from 'react';

const wrap = {
  maxWidth: 760,
  margin: '0 auto',
  padding: '2.5rem 1.25rem 4rem',
  color: '#cbd5e1',
  fontFamily: '-apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
  lineHeight: 1.65,
  fontSize: '0.98rem',
};
const h1 = { color: '#fff', fontSize: '1.9rem', margin: '0 0 0.25rem' };
const h2 = { color: '#fff', fontSize: '1.15rem', margin: '2rem 0 0.5rem' };
const muted = { color: '#94a3b8', fontSize: '0.85rem', margin: '0 0 1.5rem' };
const link = { color: '#00D4AA', textDecoration: 'none' };

// Styles for the visual opt-in mock so a reviewer can see the CTA at a glance.
const mockCard = {
  border: '1px solid #1e293b',
  background: '#111a2e',
  borderRadius: 12,
  padding: '1.25rem 1.25rem 1.4rem',
  margin: '0.75rem 0 0.5rem',
};
const mockRow = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 };
const mockLabel = { color: '#fff', fontWeight: 600, fontSize: '0.95rem' };
const field = {
  marginTop: '0.9rem', border: '1px solid #263449', background: '#0b1322',
  borderRadius: 8, padding: '0.6rem 0.75rem', color: '#e2e8f0', fontSize: '0.9rem',
};
const btn = {
  marginTop: '0.75rem', background: '#00D4AA', color: '#04121a', fontWeight: 700,
  borderRadius: 8, padding: '0.6rem 0.9rem', textAlign: 'center', fontSize: '0.9rem',
};
const consent = { color: '#94a3b8', fontSize: '0.8rem', marginTop: '0.85rem', lineHeight: 1.5 };
const sample = {
  border: '1px solid #1e293b', background: '#0b1322', borderRadius: 8,
  padding: '0.7rem 0.9rem', margin: '0.5rem 0', color: '#e2e8f0', fontSize: '0.9rem',
};

export default function SmsAlerts() {
  return (
    <div style={{ background: '#0E1525', minHeight: '100vh' }}>
      <div style={wrap}>
        <a href="/" style={{ ...link, fontSize: '0.85rem' }}>&larr; Back to AlphaGrade</a>
        <h1 style={h1}>Text Alerts (SMS)</h1>
        <p style={muted}>
          AlphaGrade &middot; operated by Hendrix, LLC &middot; alphagrade.io
        </p>

        {/* 2026-09-29: the page stays because it is the publicly published
            consent language a carrier checks during A2P vetting, and removing
            and restoring it would be churn. But it must not read as an offer
            while the channel cannot deliver: the campaign is gone and the
            number was released. Email alerts genuinely work, so say so. */}
        <div style={{
          background: 'rgba(245, 158, 11, 0.1)',
          border: '1px solid rgba(245, 158, 11, 0.4)',
          borderRadius: 8,
          padding: '0.85rem 1rem',
          margin: '1rem 0 1.25rem',
          color: '#FCD34D',
          fontSize: '0.92rem',
          lineHeight: 1.55,
        }}>
          <strong>Text alerts are not currently available.</strong> The SMS
          programme described below is paused while carrier registration is
          sorted out, and no text messages are being sent. Portfolio and Grade
          Watch alerts still arrive by <strong>email</strong>, which you can turn
          on in your alert settings. This page is kept as the published
          description of the programme.
        </div>

        <p>
          AlphaGrade is a stock and portfolio analysis web application. We offer an
          optional SMS text-alert program to our own registered account holders.
          Every message is first-party and transactional: it is about data you
          saved in your own AlphaGrade account. We do not send marketing or
          promotional texts, we do not send buy, sell, or investment
          recommendations, and this is not a purchased, rented, or third-party
          list. AlphaGrade is the sole sender and does not message on behalf of
          any third party.
        </p>

        <h2 style={h2}>What we text you</h2>
        <p>There are three kinds of message in this program:</p>
        <ol>
          <li>
            <strong>Verification code.</strong> When you enter your mobile number,
            we text you a 6-digit code to confirm you own the number and to
            complete your opt-in.
          </li>
          <li>
            <strong>Portfolio regime alerts.</strong> When a holding in your own
            saved portfolio shows a regime shift, meaning its statistical
            relationship to the broader market has moved away from its historical
            norm.
          </li>
          <li>
            <strong>Grade Watch alerts.</strong> When a ticker you added to your
            own Grade Watch list changes its AlphaGrade grade.
          </li>
        </ol>
        <p style={{ ...muted, margin: '0.5rem 0 0' }}>
          A single opt-in covers all three. Turning text alerts off stops all of
          them.
        </p>

        <h2 style={h2}>How you opt in</h2>
        <p>
          Opt-in happens inside your own logged-in AlphaGrade account. After you
          create a free account at alphagrade.io, open the{' '}
          <strong>Portfolio Alerts</strong> section and complete these steps:
        </p>
        <ol>
          <li>
            Find <strong>&ldquo;Text me alerts too.&rdquo;</strong>
          </li>
          <li>
            Enter your mobile number and press <strong>Send code</strong>.
          </li>
          <li>
            We text you a 6-digit verification code. Enter it and press{' '}
            <strong>Verify</strong> to confirm you own the number and consent to
            receive alerts. Your consent is recorded with a timestamp.
          </li>
        </ol>
        <p>
          This is the only opt-in method for this program. You can turn alerts off
          at any time with the <strong>Turn off SMS</strong> button in the same
          place, or by replying STOP to any message.
        </p>

        <p style={{ ...muted, margin: '1rem 0 0.25rem' }}>
          What the in-app opt-in looks like:
        </p>
        <div style={mockCard} aria-hidden="true">
          <div style={mockRow}>
            <span style={mockLabel}>&#128241; Text me alerts too</span>
          </div>
          <div style={field}>(555) 123&#8209;4567</div>
          <div style={btn}>Send code</div>
          <div style={field}>6&#8209;digit code</div>
          <div style={btn}>Verify</div>
          <p style={consent}>
            By enabling text alerts you agree to receive recurring automated
            portfolio alert messages from AlphaGrade at the number provided.
            Consent is not a condition of using AlphaGrade. Message frequency
            varies. Message and data rates may apply. Reply STOP to cancel or HELP
            for help. See our Terms and Privacy Policy.
          </p>
        </div>

        <h2 style={h2}>Sample messages</h2>
        <p style={{ ...muted, margin: '0 0 0.5rem' }}>
          These are the exact messages this program sends.
        </p>
        <div style={sample}>
          AlphaGrade verification code: 123456. Enter it in the app to confirm SMS
          alerts. Reply STOP to unsubscribe, HELP for help.
        </div>
        <div style={sample}>
          AlphaGrade: NVDA has decoupled from the market versus its usual pattern.
          Open the app for details. Reply STOP to opt out, HELP for help.
        </div>
        <div style={sample}>
          AlphaGrade: 2 portfolio signals today, NVDA has decoupled from the
          market, and more. See the app. Reply STOP to opt out, HELP for help.
        </div>
        <div style={sample}>
          AlphaGrade Grade Watch: AAPL moved from Promising to Neutral. See the app.
          Reply STOP to opt out, HELP for help.
        </div>
        <div style={sample}>
          AlphaGrade Grade Watch: 3 watched tickers changed grade, AAPL moved from
          Promising to Neutral, and more. Reply STOP to opt out, HELP for help.
        </div>

        <h2 style={h2}>Message frequency, rates &amp; help</h2>
        <ul>
          <li>
            Recurring messages. Frequency varies with your own holdings, your
            Grade Watch list, and market activity, and is typically a few messages
            per week.
          </li>
          <li>Message and data rates may apply.</li>
          <li>
            Reply <strong>STOP</strong> to any message to unsubscribe at any time.
            Reply <strong>HELP</strong> for help, or email{' '}
            <a href="mailto:support@alphagrade.io" style={link}>support@alphagrade.io</a>.
            You can also disable alerts anytime in your account settings.
          </li>
          <li>Carriers are not liable for delayed or undelivered messages.</li>
        </ul>

        <h2 style={h2}>Your privacy</h2>
        <p>
          <strong>
            No mobile information (including your phone number and SMS opt-in data)
            will be sold, rented, or shared with third parties or affiliates for
            marketing or promotional purposes.
          </strong>{' '}
          Your number is used solely to deliver the alerts you requested. Full
          details are in our{' '}
          <a href="/privacy" style={link}>Privacy Policy</a>, and program terms are
          in our <a href="/terms" style={link}>Terms of Service</a>.
        </p>

        <h2 style={h2}>Contact</h2>
        <p>
          Questions about the text program? Email{' '}
          <a href="mailto:support@alphagrade.io" style={link}>support@alphagrade.io</a>.
        </p>
      </div>
    </div>
  );
}
